/**
 * `QuotaBadge` — WO-V5-B critère 2 (US-029.3).
 *
 * Livré pour la zone A (pied d'`AppSidebar`, `waves/V5/B/integration-note.md` — B n'édite
 * PAS le shell) : un résumé compact d'UNE ressource de quota (valeur/limite/pourcentage),
 * lisible par tout membre. `GET /workspaces/{id}/quota` peut renvoyer plusieurs ressources
 * (espace ET IA) ; ce composant en affiche UNE, l'appelant choisit laquelle (voir
 * `QuotaPanel` pour l'écran complet listant toutes les ressources).
 */
import type { JSX } from 'react'

import { useT } from '@/i18n/language'

export interface QuotaBadgeRessource {
  label: string
  current_usage: number | null
  limit_value: number | null
  percent: number
  at_warning: boolean
  at_limit: boolean
}

interface QuotaBadgeProps {
  ressource: QuotaBadgeRessource
  /**
   * FND-CR5-03 (contre-revue G-V5, MINEUR) : le badge du panneau `/quotas` (défaut,
   * `persistant=false`) garde `role="alert"` à 100 % — annonce ponctuelle sur un écran
   * dédié. Le badge du pied de barre latérale (`QuotaBadgeSlotPied`, `persistant=true`)
   * est rendu sur TOUTES les pages : `role="alert"` y serait annoncé à répétition à
   * chaque navigation ; il porte donc `role="status"`/`aria-live="polite"` à la place
   * (annonce discrète, pas de spam pour l'utilisateur de lecteur d'écran).
   */
  persistant?: boolean
}

export function QuotaBadge({ ressource, persistant = false }: QuotaBadgeProps): JSX.Element {
  const { t } = useT()
  const ton = ressource.at_limit ? 'quota-badge--limit'
    : ressource.at_warning ? 'quota-badge--warning' : 'quota-badge--ok'
  const illimite = ressource.limit_value == null
  const role = ressource.at_limit ? (persistant ? 'status' : 'alert') : undefined

  return (
    <div className={`quota-badge ${ton}`} data-testid="quota-badge"
         role={role} aria-live={persistant && ressource.at_limit ? 'polite' : undefined}>
      <span className="quota-badge__label">{ressource.label}</span>
      <span className="quota-badge__separateur" aria-hidden="true"> : </span>
      <span className="quota-badge__valeur">
        {illimite
          ? t('quota.unlimited', 'Illimité')
          : `${ressource.current_usage ?? 0} / ${ressource.limit_value} (${ressource.percent}%)`}
      </span>
      {ressource.at_limit ? (
        <span className="quota-badge__mention">{t('quota.atLimit', 'Quota atteint')}</span>
      ) : ressource.at_warning ? (
        <span className="quota-badge__mention">{t('quota.atWarning', 'Quota bientôt atteint')}</span>
      ) : null}
    </div>
  )
}
